import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Users, MapPin, Calendar, Truck, FileText, DollarSign, 
  BarChart3, Smartphone, ArrowRight, Route, Clock, CreditCard,
  Bot, Sparkles, MessageSquare, Zap, CheckCircle
} from 'lucide-react';

const FeaturesPage = () => {
  const featureCategories = [
    {
      title: "AI-Powered Assistant",
      description: "Your intelligent business helper that understands your operations and can take action.",
      icon: Bot,
      link: "/features/ai-assistant",
      mockupType: "ai",
      highlight: true,
      features: [
        "Natural language queries - ask questions in plain English",
        "Real-time business insights and analytics",
        "Crew assignment recommendations based on location and workload",
        "Create estimates and schedule jobs through conversation",
        "Update job statuses and manage operations hands-free"
      ]
    },
    {
      title: "Customer & Property Management",
      description: "Everything you need to manage your customers and their properties in one place.",
      icon: MapPin,
      link: "/features/property-management",
      mockupType: "customers",
      aiIntegration: "Ask AI: \"Show me all properties for the Johnson account\" or \"Which customers are due for service this week?\"",
      features: [
        "Detailed customer profiles with contact info and billing details",
        "Multiple properties per customer with unique service requirements",
        "Property-specific instructions, access codes, and preferences",
        "Service history tracking for every property",
        "Map integration for property locations"
      ]
    },
    {
      title: "Scheduling & Route Optimization",
      description: "Automate your scheduling and optimize routes to save time and fuel.",
      icon: Route,
      link: "/features/scheduling",
      mockupType: "calendar",
      aiIntegration: "Ask AI: \"Schedule a lawn maintenance job for tomorrow at 9am\" or \"What jobs are on the schedule for Friday?\"",
      features: [
        "Recurring service schedules (weekly, biweekly, monthly, custom)",
        "Automatic job generation from service plans",
        "Route optimization based on property locations",
        "Drag-and-drop schedule adjustments",
        "Calendar and list views for easy planning"
      ]
    },
    {
      title: "Crew Management",
      description: "Keep your teams organized and track their productivity in real-time.",
      icon: Users,
      link: "/features/crew-management",
      mockupType: "crews",
      aiIntegration: "Ask AI: \"Assign Team Alpha to today's north side jobs\" or \"Which crew has the lightest workload?\"",
      features: [
        "Create and manage multiple crews",
        "Assign crews to routes or individual jobs",
        "Track job status and completion times",
        "View crew productivity reports",
        "Role-based permissions for staff"
      ]
    },
    {
      title: "Estimates & Invoicing",
      description: "Create professional proposals and get paid faster with integrated invoicing.",
      icon: FileText,
      link: "/features/estimates-invoicing",
      mockupType: "invoices",
      aiIntegration: "Ask AI: \"Create an estimate for $450 for lawn service\" or \"Send the Johnson estimate to the customer\"",
      features: [
        "Professional estimate creation with line items",
        "Send estimates directly to customers",
        "One-click estimate approval workflow",
        "Automatic invoice generation from completed jobs",
        "Payment tracking and recording"
      ]
    },
    {
      title: "Reporting & Analytics",
      description: "Gain insights into your business performance with comprehensive reports.",
      icon: BarChart3,
      link: "/features/reporting",
      mockupType: "reports",
      aiIntegration: "Ask AI: \"What's our revenue this month?\" or \"Show me our top 5 customers by revenue\"",
      features: [
        "Revenue and profitability reports",
        "Job completion and efficiency metrics",
        "Crew productivity analysis",
        "Estimate conversion tracking",
        "Outstanding invoice summaries"
      ]
    },
    {
      title: "Mobile Crew App",
      description: "Simple, fast mobile experience designed for field crews.",
      icon: Smartphone,
      link: "/features/mobile",
      mockupType: "mobile",
      aiIntegration: "Ask AI: \"Mark the Wilson job as completed\" or \"What's the gate code for 123 Oak St?\"",
      features: [
        "View daily route and job details",
        "One-tap job start and completion",
        "Upload before and after photos",
        "Access property instructions and gate codes",
        "Navigation to job sites"
      ]
    }
  ];

  // Mockup UI screenshots as styled components
  const renderMockup = (type) => {
    const mockups = {
      ai: (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-2 flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-white text-sm font-semibold">AI Assistant</p>
              <p className="text-emerald-100 text-xs">Online</p>
            </div>
          </div>
          <div className="p-3 bg-gray-50 space-y-2">
            <div className="flex justify-end">
              <div className="bg-emerald-600 text-white px-3 py-1.5 rounded-2xl rounded-br-md text-xs max-w-[75%]">
                Create an estimate for $450 for lawn service at 123 Oak St
              </div>
            </div>
            <div className="flex justify-start">
              <div className="bg-white shadow-sm border px-3 py-1.5 rounded-2xl rounded-bl-md text-xs max-w-[75%]">
                <div className="flex items-start gap-1.5">
                  <Bot className="h-3 w-3 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="text-gray-700">I'll create an estimate for <strong>$450</strong> for Johnson Residence at 123 Oak St.</p>
                    <div className="flex gap-1 mt-1.5">
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[10px]">✓ Created</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <div className="bg-emerald-600 text-white px-3 py-1.5 rounded-2xl rounded-br-md text-xs max-w-[75%]">
                Now assign Team Alpha to that job
              </div>
            </div>
            <div className="flex justify-start">
              <div className="bg-white shadow-sm border px-3 py-1.5 rounded-2xl rounded-bl-md text-xs max-w-[75%]">
                <div className="flex items-start gap-1.5">
                  <Bot className="h-3 w-3 text-emerald-600 mt-0.5" />
                  <p className="text-gray-700">✅ Done! Team Alpha assigned to Johnson job.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
      customers: (
        <div className="bg-white rounded-lg shadow-lg p-4 h-full">
          <div className="flex items-center justify-between mb-4 pb-2 border-b">
            <h4 className="font-semibold text-gray-800">Customers</h4>
            <div className="bg-emerald-500 text-white text-xs px-2 py-1 rounded">+ Add</div>
          </div>
          <div className="space-y-2">
            {['Johnson Residence', 'Smith Property', 'Green Valley HOA'].map((name, i) => (
              <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-xs font-bold">
                  {name[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{name}</p>
                  <p className="text-xs text-gray-500">{i + 1} properties</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
      calendar: (
        <div className="bg-white rounded-lg shadow-lg p-4 h-full">
          <div className="flex items-center justify-between mb-4 pb-2 border-b">
            <h4 className="font-semibold text-gray-800">Today's Schedule</h4>
            <div className="text-xs text-gray-500">Mon, Jun 15</div>
          </div>
          <div className="space-y-2">
            {[
              { time: '8:00 AM', job: '123 Oak St', status: 'done' },
              { time: '10:00 AM', job: '456 Maple Ave', status: 'active' },
              { time: '1:00 PM', job: '789 Pine Rd', status: 'pending' }
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded bg-gray-50">
                <div className={`w-2 h-2 rounded-full ${item.status === 'done' ? 'bg-emerald-500' : item.status === 'active' ? 'bg-amber-500' : 'bg-gray-300'}`}></div>
                <div className="text-xs text-gray-500 w-16">{item.time}</div>
                <div className="text-sm text-gray-800">{item.job}</div>
              </div>
            ))}
          </div>
        </div>
      ),
      crews: (
        <div className="bg-white rounded-lg shadow-lg p-4 h-full">
          <div className="flex items-center justify-between mb-4 pb-2 border-b">
            <h4 className="font-semibold text-gray-800">Active Crews</h4>
          </div>
          <div className="space-y-2">
            {[
              { name: 'Team Alpha', status: 'On Route', jobs: 6 },
              { name: 'Team Bravo', status: 'Break', jobs: 4 },
              { name: 'Team Charlie', status: 'On Route', jobs: 5 }
            ].map((crew, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{crew.name}</p>
                    <p className="text-xs text-gray-500">{crew.jobs} jobs</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${crew.status === 'On Route' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {crew.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      ),
      invoices: (
        <div className="bg-white rounded-lg shadow-lg p-4 h-full">
          <div className="flex items-center justify-between mb-4 pb-2 border-b">
            <h4 className="font-semibold text-gray-800">Invoice #1234</h4>
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Paid</span>
          </div>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Lawn Service</span>
              <span>$75.00</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Hedge Trimming</span>
              <span>$120.00</span>
            </div>
          </div>
          <div className="border-t pt-2 flex justify-between">
            <span className="font-semibold text-gray-800">Total</span>
            <span className="font-bold text-emerald-600">$195.00</span>
          </div>
        </div>
      ),
      reports: (
        <div className="bg-white rounded-lg shadow-lg p-4 h-full">
          <div className="flex items-center justify-between mb-4 pb-2 border-b">
            <h4 className="font-semibold text-gray-800">Revenue</h4>
            <span className="text-xs text-emerald-600">+12% this month</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-emerald-50 p-2 rounded text-center">
              <p className="text-xl font-bold text-emerald-700">$48K</p>
              <p className="text-xs text-gray-500">This Month</p>
            </div>
            <div className="bg-blue-50 p-2 rounded text-center">
              <p className="text-xl font-bold text-blue-700">387</p>
              <p className="text-xs text-gray-500">Jobs Done</p>
            </div>
          </div>
          <div className="flex gap-1 h-16 items-end">
            {[40, 65, 45, 80, 70, 90, 85].map((h, i) => (
              <div key={i} className="flex-1 bg-emerald-400 rounded-t" style={{ height: `${h}%` }}></div>
            ))}
          </div>
        </div>
      ),
      mobile: (
        <div className="bg-gray-900 rounded-2xl p-2 mx-auto" style={{ maxWidth: '180px' }}>
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="bg-emerald-500 text-white p-3 text-center">
              <p className="text-xs opacity-80">Today's Route</p>
              <p className="font-semibold text-sm">Team Alpha</p>
            </div>
            <div className="p-2 space-y-1">
              {['Done', 'In Progress', 'Next'].map((status, i) => (
                <div key={i} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded text-xs">
                  <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-emerald-500' : i === 1 ? 'bg-amber-500' : 'bg-gray-300'}`}></div>
                  <span className="text-gray-700">{['123 Oak St', '456 Maple', '789 Pine'][i]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    };
    return mockups[type] || null;
  };

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
            Features Built for Landscapers
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Orchard LMS is designed specifically for recurring, route-based landscape services. 
            Every feature is built around how landscaping companies actually work.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-primary hover:bg-primary/90">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Feature Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {featureCategories.map((category, index) => (
              <div 
                key={index} 
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} ${category.highlight ? 'bg-gradient-to-r from-violet-50 to-indigo-50 -mx-4 px-4 py-12 rounded-3xl sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  {category.highlight && (
                    <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                      <Sparkles className="h-4 w-4" />
                      Core Feature
                    </div>
                  )}
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${category.highlight ? 'bg-violet-600' : 'bg-primary/10'}`}>
                    <category.icon className={`h-7 w-7 ${category.highlight ? 'text-white' : 'text-primary'}`} />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">{category.title}</h2>
                  <p className="text-lg text-gray-600 mb-6">{category.description}</p>
                  <ul className="space-y-3 mb-6">
                    {category.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${category.highlight ? 'bg-violet-100' : 'bg-primary/10'}`}>
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                        </div>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* AI Integration Callout */}
                  {category.aiIntegration && (
                    <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4 text-violet-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-violet-900 mb-1">Works with AI Assistant</p>
                          <p className="text-sm text-violet-700">{category.aiIntegration}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Link to={category.link}>
                    <Button variant="outline">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
                <div className={`bg-gray-100 rounded-2xl p-6 flex items-center justify-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  {renderMockup(category.mockupType)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to See It in Action?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start your free account today and experience the difference purpose-built software makes.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default FeaturesPage;
